import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Button,
    Alert,
    LinearProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

/**
 * Parses a CSV string into { headers, rows }.
 */
function parseCsv(text) {
    const lines = text.trim().split(/\r?\n/);
    if (lines.length === 0) return { headers: [], rows: [] };
    const headers = lines[0].split(',').map((h) => h.replace(/^"|"$/g, '').trim());
    const rows = lines.slice(1).map((line) => {
        const values = line.split(',').map((v) => v.replace(/^"|"$/g, '').trim());
        return headers.reduce((obj, header, i) => {
            obj[header] = values[i] ?? '';
            return obj;
        }, {});
    });
    return { headers, rows };
}

export default function ImportStepUpload({ typeConfig, onParsed, onError, onNext, uploadError }) {
    const [t] = useTranslation();
    const inputRef = useRef(null);
    const [fileName, setFileName] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    const acceptedFormats = typeConfig?.acceptedFormats || ['.csv'];
    const accept = acceptedFormats.join(',');

    const processFile = (file) => {
        if (!file) return;

        const ext = '.' + file.name.split('.').pop().toLowerCase();
        if (!acceptedFormats.includes(ext)) {
            onError(t('Unsupported file format. Accepted: {{formats}}', { formats: acceptedFormats.join(', ') }));
            return;
        }

        setFileName(file.name);
        setParsing(true);
        onError(null);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const { headers, rows } = parseCsv(e.target.result);
                if (headers.length === 0) {
                    onError(t('The file appears to be empty or invalid.'));
                } else {
                    onParsed(headers, rows);
                }
            } catch {
                onError(t('Failed to parse file. Please check the format.'));
            } finally {
                setParsing(false);
            }
        };
        reader.readAsText(file);
    };

    const handleFileChange = (e) => processFile(e.target.files?.[0]);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        processFile(e.dataTransfer.files?.[0]);
    };

    return (
        <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
                {t('Upload a CSV file to import {{label}} data.', { label: t(typeConfig?.label || '') })}
            </Typography>

            {/* Drop zone */}
            <Box
                onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
                onClick={() => inputRef.current?.click()}
                sx={{
                    border: '2px dashed',
                    borderColor: isDragOver ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 5,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragOver ? 'action.hover' : 'background.paper',
                    transition: 'all 0.15s',
                    '&:hover': { borderColor: 'primary.main', backgroundColor: 'action.hover' },
                }}
            >
                <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body1" color="text.secondary">
                    {fileName
                        ? fileName
                        : t('Drag & drop your file here, or click to browse')}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                    {t('Supported formats')}: {acceptedFormats.join(', ')}
                </Typography>
            </Box>

            <input
                ref={inputRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={handleFileChange}
            />

            {parsing && <LinearProgress sx={{ mt: 2 }} />}
            {uploadError && <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>}

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={onNext}
                    disabled={!fileName || parsing || !!uploadError}
                >
                    {t('Next')}
                </Button>
            </Box>
        </Box>
    );
}
